import theme from "theme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import LanguageProvider from "providers/language";
import AuthProvider from "providers/auth";
import { ImpersonationProvider } from "providers/impersonation";
import Pages from "pages";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "query-client";
import LoadingProvider from "providers/loading";
import SnackbarProvider from "providers/snackbar";
import { ViewingOtherSpecialistInformationContextProvider } from "providers/viewing-other-specialist-information";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { useCookieConsent } from "hooks/use-cookie-consent";
import { useLanguage } from "providers/language";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useEffect, useMemo } from "react";
import type { Dayjs } from "dayjs";
import type { PickersInputLocaleText } from "@mui/x-date-pickers/locales";
import { useDictionary } from "hooks/use-dictionary";
import { interpolate } from "utils/interpolate";

const AppContent = () => {
  const { lang } = useLanguage();
  const dict = useDictionary();

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = dict.header.competencePlatform;
  }, [lang, dict.header.competencePlatform]);

  const localizedTheme = useMemo(() => {
    const pluralRules = new Intl.PluralRules(lang);
    return createTheme(theme, {
      components: {
        MuiAutocomplete: {
          defaultProps: {
            clearText: dict.libraries.autocomplete.clear,
            openText: dict.libraries.autocomplete.open,
            closeText: dict.common.ariaLabels.closeButton,
            noOptionsText: dict.common.noOptions,
            loadingText: dict.common.loading,
          },
        },
        MuiPagination: {
          defaultProps: {
            "aria-label": dict.libraries.pagination.navigation,
            getItemAriaLabel: (
              type: "page" | "first" | "last" | "next" | "previous",
              page: number | null,
              selected: boolean,
            ) => {
              switch (type) {
                case "page":
                  return interpolate(
                    selected ? dict.libraries.pagination.currentPage : dict.common.ariaLabels.pageNumber,
                    { value: String(page) },
                  );
                case "first":
                  return dict.libraries.pagination.firstPage;
                case "last":
                  return dict.libraries.pagination.lastPage;
                case "next":
                  return dict.common.ariaLabels.nextPage;
                case "previous":
                  return dict.common.ariaLabels.previousPage;
              }
            },
          },
        },
        MuiRating: {
          defaultProps: {
            emptyLabelText: dict.libraries.rating.empty,
            getLabelText: (value: number) => {
              const category = pluralRules.select(value);
              const template = category === "one"
                ? dict.libraries.rating.one
                : category === "few"
                  ? dict.libraries.rating.few
                  : dict.libraries.rating.other;
              return interpolate(template, { value });
            },
          },
        },
      },
    });
  }, [dict, lang]);

  const pickerLocaleText = useMemo<PickersInputLocaleText<Dayjs>>(() => {
    const text = dict.libraries.datePicker;
    return {
      previousMonth: text.previousMonth,
      nextMonth: text.nextMonth,
      openPreviousView: text.openPreviousView,
      openNextView: text.openNextView,
      calendarViewSwitchingButtonAriaLabel: (view) =>
        view === "year" ? text.switchToCalendar : text.switchToYear,
      cancelButtonLabel: dict.common.cancel,
      clearButtonLabel: text.clearButtonLabel,
      okButtonLabel: text.okButtonLabel,
      todayButtonLabel: text.todayButtonLabel,
      datePickerToolbarTitle: text.datePickerToolbarTitle,
      calendarWeekNumberHeaderLabel: text.calendarWeekNumberHeaderLabel,
      calendarWeekNumberHeaderText: "#",
      calendarWeekNumberAriaLabelText: (weekNumber) =>
        interpolate(text.calendarWeekNumberAriaLabelText, { value: weekNumber }),
      calendarWeekNumberText: (weekNumber) => String(weekNumber),
      openDatePickerDialogue: (value, utils, formattedDate) => {
        const selectedDate = formattedDate ??
          (value !== null && utils.isValid(value) ? utils.format(value, "fullDate") : null);
        return selectedDate
          ? interpolate(text.chooseSelectedDate, { value: selectedDate })
          : text.chooseDate;
      },
      fieldClearLabel: text.fieldClearLabel,
      dateTableLabel: text.dateTableLabel,
      fieldYearPlaceholder: ({ digitAmount }) => "Y".repeat(digitAmount),
      fieldMonthPlaceholder: ({ contentType }) => contentType === "letter" ? "MMMM" : "MM",
      fieldDayPlaceholder: () => "DD",
      fieldWeekDayPlaceholder: ({ contentType }) => contentType === "letter" ? "EEEE" : "EE",
      year: text.year,
      month: text.month,
      day: text.day,
      weekDay: text.weekDay,
      empty: text.empty,
    };
  }, [dict]);

  useCookieConsent();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={localizedTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang} localeText={pickerLocaleText}>
          <SnackbarProvider>
            <LoadingProvider>
              <AuthProvider>
                <ImpersonationProvider>
                  <ViewingOtherSpecialistInformationContextProvider>
                    <Pages />
                  </ViewingOtherSpecialistInformationContextProvider>
                </ImpersonationProvider>
              </AuthProvider>
            </LoadingProvider>
          </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

const App = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

export default App;
